// src/lib/authHelper.ts
import { collection, doc, getDocs, query, serverTimestamp, setDoc, updateDoc } from 'firebase/firestore';
import { db } from './firebase';
import type { User } from '../types/schema';
import { normalizeSwedishPhoneNumber } from './phoneNumbers';

export const authHelper = {
  async checkIfFirstUser(): Promise<boolean> {
    try {
      const usersRef = collection(db, 'users');
      const snapshot = await getDocs(usersRef);
      return snapshot.empty;
    } catch (error) {
      console.error('Error checking first user:', error);
      return false;
    }
  },

  async findUsersByPhoneNumber(phoneNumber: string | null): Promise<User[]> {
    try {
      const normalizedPhoneNumber = normalizeSwedishPhoneNumber(phoneNumber);

      if (!normalizedPhoneNumber) {
        return [];
      }

      const usersRef = collection(db, 'users');
      const snapshot = await getDocs(query(usersRef));

      return snapshot.docs
        .map((userDoc) => ({ id: userDoc.id, ...userDoc.data() } as User))
        .filter((user) => normalizeSwedishPhoneNumber(user.phoneNumber) === normalizedPhoneNumber);
    } catch (error) {
      console.error('Error in findUsersByPhoneNumber:', error);
      throw error;
    }
  },

  async updateUserLastLogin(userId: string): Promise<void> {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        lastLoginAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error in updateUserLastLogin:', error);
      throw error;
    }
  },

  async upsertAuthLink(authUid: string, userId: string, phoneNumber: string | null): Promise<void> {
    try {
      const normalizedPhoneNumber = normalizeSwedishPhoneNumber(phoneNumber);

      if (!authUid || !userId || !normalizedPhoneNumber) {
        throw new Error('Missing auth link data.');
      }

      const authLinkRef = doc(db, 'authLinks', authUid);
      await setDoc(authLinkRef, {
        authUid,
        userId,
        phoneNumber: normalizedPhoneNumber,
        updatedAt: serverTimestamp()
      }, { merge: true });
    } catch (error) {
      console.error('Error in upsertAuthLink:', error);
      throw error;
    }
  }
};
